import { FC } from 'react';
import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import { formatNumber } from 'utils/number';
import type { AfeCatalogSummary } from './types';
import styles from './summary-cards.module.scss';

interface SummaryCardsProps {
  totalCount: number | undefined;
  summary: AfeCatalogSummary | undefined;
}

/**
 * Renders 6 KPI summary cards for the AFE Catalog page.
 * Card 1 displays the total AFE count as an integer.
 * Cards 2-6 display average cost metrics formatted as currency.
 * All values reflect the currently filtered dataset.
 * @param {SummaryCardsProps} props - The component props.
 * @return {JSX.Element} A grid of 6 SummaryStatCard components.
 */
const SummaryCards: FC<SummaryCardsProps> = ({ totalCount, summary }) => {
  return (
    <div className={styles.cardsContainer}>
      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Total AFEs"
          value={formatNumber({
            value: totalCount,
            minPrecision: 0,
            maxPrecision: 0,
          })}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Average AFE Costs"
          value={formatNumber({
            value: summary?.avgAfeCost,
            minPrecision: 0,
            maxPrecision: 0,
            style: 'currency',
          })}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Average Drilling Cost"
          value={formatNumber({
            value: summary?.avgDrillingCost,
            minPrecision: 0,
            maxPrecision: 0,
            style: 'currency',
          })}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Average Completion Cost"
          value={formatNumber({
            value: summary?.avgCompletionCost,
            minPrecision: 0,
            maxPrecision: 0,
            style: 'currency',
          })}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Average Facilities Cost"
          value={formatNumber({
            value: summary?.avgFacilitiesCost,
            minPrecision: 0,
            maxPrecision: 0,
            style: 'currency',
          })}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Average Contingency & Overhead"
          value={formatNumber({
            value: summary?.avgContingenciesCost,
            minPrecision: 0,
            maxPrecision: 0,
            style: 'currency',
          })}
        />
      </div>
    </div>
  );
};

export { SummaryCards };
